import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScenarioStore } from '../store/useScenarioStore';
import { useAppModeStore } from '../store/useAppModeStore';
import * as api from '../utils/api';
import {
  AccentCard,
  Eyebrow,
  DisclosureRow,
  LedgerRow,
  Stat,
  type LedgerRowVariant,
} from '../components/design';
import { GroupedYearByCostChart, deriveYearsFromFleetResponse } from '../components/compare/YearByCostChart';
import ExportCsvButton from '../components/ExportCsvButton';
import ExportPdfButton from '../components/ExportPdfButton';
import type {
  FleetTotals,
  ScenarioComparisonResponse,
  ScenarioInput,
  ScenarioSummary,
  TopDriver,
  SensitivityTestResult,
  FleetTCOResponse,
  FleetLineOutput,
} from '../types';

// Phase 22.5 cost-line display convention (preserved through Phase 25):
//
//   one_time        : single dollar amount paid at t=0; no subtext.
//                     (acquisition)
//   one_time_credit : single credit reclaimed at end-of-life; subtext
//                     shows per-vehicle. Higher = better.
//                     (residual)
//   annual          : already a per-year value; subtext shows total over
//                     the ownership period (annual × years).
//                     (total_annual_*)
//   lifetime_total  : sum across the ownership period; subtext shows
//                     annualized (/yr).
//                     (total_tco_over_period)
type CostLineKind = 'one_time' | 'one_time_credit' | 'annual' | 'lifetime_total';

const COST_LINES: {
  key: keyof FleetTotals;
  label: string;
  kind: CostLineKind;
  // direction-of-good. Cost lines: lower = better. Residual credit:
  // higher = better. Used to decide which scenario "wins" each row,
  // independent of the raw delta sign.
  lowerBetter: boolean;
}[] = [
  { key: 'total_acquisition_cost',          label: 'Total Acquisition',           kind: 'one_time',        lowerBetter: true  },
  { key: 'total_annual_fuel_cost',          label: 'Annual Fuel',                 kind: 'annual',          lowerBetter: true  },
  { key: 'total_annual_idle_fuel_cost',     label: 'Annual Idle Fuel',            kind: 'annual',          lowerBetter: true  },
  { key: 'total_annual_labor_cost',         label: 'Annual Labor',                kind: 'annual',          lowerBetter: true  },
  { key: 'total_annual_maintenance_cost',   label: 'Annual Maintenance',          kind: 'annual',          lowerBetter: true  },
  { key: 'total_annual_insurance_cost',     label: 'Annual Insurance',            kind: 'annual',          lowerBetter: true  },
  { key: 'total_annual_registration_cost',  label: 'Annual Registration',         kind: 'annual',          lowerBetter: true  },
  { key: 'total_annual_operating_cost',     label: 'Total Operating / Year',      kind: 'annual',          lowerBetter: true  },
  { key: 'total_residual_reclaimed',        label: 'Residual Value Reclaimed',    kind: 'one_time_credit', lowerBetter: false },
  // Phase 24.1: Section 179 + bonus depreciation tax benefit. Year-1 federal
  // cash credit (higher = better). Filtered in render: hidden when !isFleet
  // (mirrors the single-scenario TCOSummaryTable gate) or when both
  // scenarios show $0 (tax-exempt or all-ineligible).
  { key: 'total_tax_benefit',               label: 'Tax Benefit (Sec. 179 + Bonus)', kind: 'one_time_credit', lowerBetter: false },
  { key: 'total_tco_over_period',           label: 'Total TCO (period)',          kind: 'lifetime_total',  lowerBetter: true  },
];

const LEDGER_GRID = '2fr 1.3fr 1.3fr 1.3fr 0.8fr';

function fmtMoney(v: number): string {
  if (!isFinite(v)) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtMoneyPlain(v: number): string {
  if (!isFinite(v)) return '—';
  const abs = Math.abs(Math.round(v));
  return (v < 0 ? '−$' : '$') + abs.toLocaleString('en-US');
}

function fmtSignedDelta(value: number): string {
  if (!isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${fmtMoney(Math.abs(value))}`;
}

function fmtAnnualizedHint(total: number, ownershipYears: number): string {
  if (!isFinite(total) || ownershipYears <= 0) return '';
  const perYear = total / ownershipYears;
  return `${fmtMoney(perYear)}/yr`;
}

// Decide which scenario wins a row based on direction-of-good. Equality
// within $1 is treated as a tie. Phase 22.5 convention.
function rowWinner(a: number, b: number, lowerBetter: boolean): 'a' | 'b' | 'tie' {
  if (Math.abs(a - b) < 1) return 'tie';
  if (lowerBetter) return a < b ? 'a' : 'b';
  return a > b ? 'a' : 'b';
}

// Subtext per cost-line kind. See the COST_LINES kind comment block.
function subtextFor(
  kind: CostLineKind,
  value: number,
  ownershipYears: number,
  vehicleCount: number,
): string {
  if (!isFinite(value)) return '';
  switch (kind) {
    case 'one_time':
      return '';
    case 'one_time_credit': {
      if (vehicleCount <= 0) return '';
      const perVehicle = value / vehicleCount;
      return `${fmtMoney(perVehicle)} per vehicle`;
    }
    case 'annual': {
      if (ownershipYears <= 0) return '';
      const total = value * ownershipYears;
      const yearsLabel = ownershipYears === 1 ? 'year' : 'years';
      return `= ${fmtMoney(total)} over ${ownershipYears.toFixed(0)} ${yearsLabel}`;
    }
    case 'lifetime_total':
      return fmtAnnualizedHint(value, ownershipYears);
  }
}

const PAGE_GAP = 18;

// Hover/focus tooltip styled for the Phase 25 ledger theme. Replaces native
// `title` attributes which had a slow browser delay and missed clicks.
function ResidualInfoTooltip() {
  return (
    <span
      tabIndex={0}
      role="button"
      aria-label="Residual value source"
      className="relative group"
      style={{
        marginLeft: 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: 'var(--tco-line-soft)',
        color: 'var(--tco-ink-3)',
        fontSize: 10,
        fontWeight: 700,
        cursor: 'help',
        border: '1px solid var(--tco-line)',
        verticalAlign: 'middle',
      }}
    >
      i
      <span
        role="tooltip"
        className="invisible group-hover:visible group-focus:visible opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity pointer-events-none"
        style={{
          position: 'absolute',
          left: 0,
          top: '100%',
          marginTop: 8,
          width: 280,
          zIndex: 30,
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          color: 'var(--tco-ink)',
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: 'normal',
          textTransform: 'none',
          boxShadow: '0 8px 24px rgba(23, 24, 26, 0.12)',
        }}
      >
        Estimated value retained at end of ownership period, based on vehicle
        class, powertrain, age, and accumulated mileage. Reduces total TCO.
        <span style={{ display: 'block', marginTop: 6, fontSize: 11, fontStyle: 'italic', color: 'var(--tco-ink-3)' }}>
          Source: Argonne National Laboratory ANL/ESD-22/2
        </span>
      </span>
    </span>
  );
}

// Phase 24.1: tooltip for the Section 179 + bonus depreciation row. Mirrors
// the wording from the single-scenario TCOSummaryTable so the two surfaces
// stay consistent.
function TaxBenefitInfoTooltip() {
  return (
    <span
      tabIndex={0}
      role="button"
      aria-label="Tax benefit source"
      className="relative group"
      style={{
        marginLeft: 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: 'var(--tco-line-soft)',
        color: 'var(--tco-ink-3)',
        fontSize: 10,
        fontWeight: 700,
        cursor: 'help',
        border: '1px solid var(--tco-line)',
        verticalAlign: 'middle',
      }}
    >
      i
      <span
        role="tooltip"
        className="invisible group-hover:visible group-focus:visible opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity pointer-events-none"
        style={{
          position: 'absolute',
          left: 0,
          top: '100%',
          marginTop: 8,
          width: 280,
          zIndex: 30,
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          color: 'var(--tco-ink)',
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: 'normal',
          textTransform: 'none',
          boxShadow: '0 8px 24px rgba(23, 24, 26, 0.12)',
        }}
      >
        First-year federal tax deduction from Section 179 expensing and bonus
        depreciation, multiplied by your marginal tax rate. Reduces effective
        acquisition cost. Federal only — state conformity and MACRS years 2–7
        are not modeled.
        <span style={{ display: 'block', marginTop: 6, fontSize: 11, fontStyle: 'italic', color: 'var(--tco-ink-3)' }}>
          Sources: IRC §179, IRC §168(k), IRS Rev. Proc. 2024-21
        </span>
      </span>
    </span>
  );
}

export default function ScenarioComparePage() {
  const navigate = useNavigate();
  const { scenarios, loadScenarios, loading } = useScenarioStore();
  // Phase 24.1: gate the Tax Benefit row identically to the single-scenario
  // TCOSummaryTable — only render in Fleet mode.
  const activeMode = useAppModeStore((s) => s.activeMode);
  const isFleet = activeMode === 'fleet';

  const [scenarioAId, setScenarioAId] = useState<string | number | ''>('');
  const [scenarioBId, setScenarioBId] = useState<string | number | ''>('');
  const [scenarioA, setScenarioA] = useState<ScenarioInput | null>(null);
  const [scenarioB, setScenarioB] = useState<ScenarioInput | null>(null);
  const [comparison, setComparison] = useState<ScenarioComparisonResponse | null>(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  // Phase 24.1: cross-mode selection block. The compare picker only offers
  // scenarios that match the active app mode so a Personal scenario can never
  // be compared against a Fleet scenario (the underlying calc would mix two
  // unrelated metric stacks). Switching modes mid-session clears whatever
  // was previously selected.
  const availableScenarios = useMemo(
    () =>
      scenarios.filter(
        (s) => (s.mode === 'fleet' ? 'fleet' : 'individual') === activeMode,
      ),
    [scenarios, activeMode],
  );

  useEffect(() => {
    // When the active mode flips, drop any selection that's no longer in
    // the filtered list — and clear the prior comparison so stale results
    // don't linger on screen.
    const ids = new Set(availableScenarios.map((s) => s.id));
    if (scenarioAId && !ids.has(scenarioAId)) setScenarioAId('');
    if (scenarioBId && !ids.has(scenarioBId)) setScenarioBId('');
    if (
      (scenarioAId && !ids.has(scenarioAId)) ||
      (scenarioBId && !ids.has(scenarioBId))
    ) {
      setComparison(null);
      setScenarioA(null);
      setScenarioB(null);
    }
  }, [availableScenarios, scenarioAId, scenarioBId]);

  useEffect(() => {
    if (availableScenarios.length >= 1 && !scenarioAId) setScenarioAId(availableScenarios[0].id);
    if (availableScenarios.length >= 2 && !scenarioBId) setScenarioBId(availableScenarios[1].id);
  }, [availableScenarios, scenarioAId, scenarioBId]);

  async function compareSelected() {
    if (!scenarioAId || !scenarioBId) return;
    setComputing(true);
    setError(null);
    setComparison(null);
    try {
      const [detailA, detailB] = await Promise.all([
        api.getScenario(scenarioAId),
        api.getScenario(scenarioBId),
      ]);
      const sa = detailA.scenario;
      const sb = detailB.scenario;
      setScenarioA(sa);
      setScenarioB(sb);
      const result = await api.computeScenarioComparison(sa, sb);
      setComparison(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setComputing(false);
    }
  }

  const resultA = comparison?.result_a ?? null;
  const resultB = comparison?.result_b ?? null;
  const headlineDelta = comparison?.comparison.headline_delta ?? null;
  const ownershipPeriodA = resultA?.fleet_totals.ownership_period_years ?? 0;
  const ownershipPeriodB = resultB?.fleet_totals.ownership_period_years ?? 0;
  const aName = scenarioA?.name ?? 'Scenario A';
  const bName = scenarioB?.name ?? 'Scenario B';

  const ledgerRows = useMemo<LedgerRowVariant[]>(() => {
    if (!resultA || !resultB) return [];
    return COST_LINES.filter(({ key }) => {
      // Same gate as TCOSummaryTable: hide tax benefit when not in fleet
      // mode, or when both scenarios show no benefit.
      if (key !== 'total_tax_benefit') return true;
      if (!isFleet) return false;
      const a = Number(resultA.fleet_totals[key] ?? 0);
      const b = Number(resultB.fleet_totals[key] ?? 0);
      return a > 0 || b > 0;
    }).map(({ key, label, kind, lowerBetter }) => {
      const a = Number(resultA.fleet_totals[key] ?? 0);
      const b = Number(resultB.fleet_totals[key] ?? 0);
      const winner = rowWinner(a, b, lowerBetter);
      const aSub = subtextFor(kind, a, ownershipPeriodA, resultA.fleet_totals.vehicle_count);
      const bSub = subtextFor(kind, b, ownershipPeriodB, resultB.fleet_totals.vehicle_count);
      const winnerName =
        winner === 'a' ? aName : winner === 'b' ? bName : null;
      const isTotals = key === 'total_tco_over_period';
      const trailing =
        key === 'total_residual_reclaimed' ? <ResidualInfoTooltip /> :
        key === 'total_tax_benefit' ? <TaxBenefitInfoTooltip /> :
        undefined;
      return {
        kind: isTotals ? 'totals' : 'numeric',
        label,
        labelTrailing: trailing,
        a, b,
        aSubtext: aSub || undefined,
        bSubtext: bSub || undefined,
        deltaSubtext: winnerName ? `${winnerName} wins` : 'tie',
        higherBetter: !lowerBetter,
        winner,
      } as LedgerRowVariant;
    });
  }, [resultA, resultB, ownershipPeriodA, ownershipPeriodB, aName, bName, isFleet]);

  if (!loading && availableScenarios.length === 0) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--tco-ink)', marginBottom: 8 }}>
          No saved {isFleet ? 'fleet' : 'personal'} scenarios
        </h1>
        <p style={{ fontSize: 14, color: 'var(--tco-ink-2)', marginBottom: 24 }}>
          Save at least two {isFleet ? 'fleet' : 'personal'} scenarios to compare them side-by-side.
        </p>
        <button onClick={() => navigate('/')} style={primaryBtn}>
          Create a Scenario
        </button>
      </div>
    );
  }

  if (!loading && availableScenarios.length === 1) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--tco-ink)', marginBottom: 8 }}>
          Save another {isFleet ? 'fleet' : 'personal'} scenario to enable comparison
        </h1>
        <p style={{ fontSize: 14, color: 'var(--tco-ink-2)', marginBottom: 24 }}>
          You currently have only one saved {isFleet ? 'fleet' : 'personal'} scenario.
        </p>
        <button onClick={() => navigate('/')} style={primaryBtn}>
          Create Another Scenario
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1140, margin: '0 auto', padding: '32px 32px 48px', display: 'flex', flexDirection: 'column', gap: PAGE_GAP }}>
      {/* 1.2 Page title */}
      <div>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: '-0.015em', color: 'var(--tco-ink)' }}>
          Scenario Comparison
        </h1>
        <div style={{ marginTop: 6, fontSize: 14, color: 'var(--tco-ink-2)' }}>
          {isFleet
            ? 'Side-by-side fleet TCO comparison between two saved scenarios.'
            : 'Side-by-side TCO comparison between two saved scenarios.'}
        </div>
      </div>

      {/* 1.3 Scenario A / B selectors */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <ScenarioPicker label="Scenario A" scenarioKey="a" value={scenarioAId} onChange={setScenarioAId} scenarios={availableScenarios} />
          <ScenarioPicker label="Scenario B" scenarioKey="b" value={scenarioBId} onChange={setScenarioBId} scenarios={availableScenarios} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
          {scenarioAId && scenarioBId && scenarioAId === scenarioBId && (
            <span style={{ fontSize: 12, color: 'var(--tco-neg)' }}>
              Pick two different scenarios to compare.
            </span>
          )}
          <button
            onClick={compareSelected}
            disabled={computing || !scenarioAId || !scenarioBId || scenarioAId === scenarioBId}
            style={{
              ...redBtn,
              opacity: computing || !scenarioAId || !scenarioBId || scenarioAId === scenarioBId ? 0.45 : 1,
              cursor: computing ? 'wait' : 'pointer',
            }}
          >
            {computing ? 'COMPUTING…' : 'COMPARE'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <AccentCard stripe="red" padding="12px 18px">
          <div style={{ fontSize: 13, color: 'var(--tco-neg)' }}>{error}</div>
        </AccentCard>
      )}

      {/* Loading skeleton */}
      {computing && !comparison && (
        <>
          <SkeletonHeadline />
          <SkeletonLedger />
        </>
      )}

      {/* Empty (pre-compare) state */}
      {!computing && !comparison && !error && (
        <AccentCard padding="24px 28px">
          <div style={{ fontSize: 14, color: 'var(--tco-ink-2)' }}>
            Select two scenarios above and press <strong style={{ color: 'var(--tco-ink)' }}>COMPARE</strong> to see a
            side-by-side diff.
          </div>
        </AccentCard>
      )}

      {/* 1.4 Breakeven info chip — ownership period summary */}
      {comparison && (
        <OwnershipPeriodChip
          aYears={ownershipPeriodA}
          bYears={ownershipPeriodB}
          aName={aName}
          bName={bName}
          info={comparison.comparison.ownership_period}
        />
      )}

      {/* Phase 26.1: Export CSV of the compare result */}
      {comparison && scenarioA && scenarioB && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <ExportCsvButton
            kind="compare"
            scenarioA={scenarioA}
            scenarioB={scenarioB}
            comparison={comparison}
            mode={isFleet ? 'fleet' : 'personal'}
          />
          <ExportPdfButton
            kind="compare"
            scenarioA={scenarioA}
            scenarioB={scenarioB}
            comparison={comparison}
            mode={isFleet ? 'fleet' : 'personal'}
          />
        </div>
      )}

      {/* 1.5 Headline TCO Delta */}
      {headlineDelta && resultA && resultB && (
        <HeadlineCard
          aName={aName}
          bName={bName}
          aTotal={headlineDelta.a}
          bTotal={headlineDelta.b}
          dollarDelta={headlineDelta.dollar_delta}
          pctDelta={headlineDelta.pct_delta}
          aPerVehicle={
            resultA.fleet_totals.vehicle_count > 0
              ? headlineDelta.a / resultA.fleet_totals.vehicle_count
              : null
          }
          bPerVehicle={
            resultB.fleet_totals.vehicle_count > 0
              ? headlineDelta.b / resultB.fleet_totals.vehicle_count
              : null
          }
          topDrivers={comparison?.comparison.top_drivers ?? []}
          horizonYears={Math.max(ownershipPeriodA, ownershipPeriodB)}
        />
      )}

      {/* 1.6 Cost ledger */}
      {ledgerRows.length > 0 && resultA && resultB && (
        <div
          style={{
            background: 'var(--tco-surface)',
            border: '1px solid var(--tco-line)',
            borderRadius: 'var(--tco-radius)',
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: LEDGER_GRID,
              background: 'var(--tco-line-soft)',
              borderBottom: '1px solid var(--tco-line)',
            }}
          >
            <div style={{ padding: '11px 18px' }}>
              <Eyebrow>Cost Line</Eyebrow>
            </div>
            <div style={{
              padding: '11px 18px',
              textAlign: 'right',
              background: 'var(--scenario-a-tint)',
              borderBottom: '2px solid var(--scenario-a-border)',
            }}>
              <Eyebrow>Scenario A</Eyebrow>
            </div>
            <div style={{
              padding: '11px 18px',
              textAlign: 'right',
              background: 'var(--scenario-b-tint)',
              borderBottom: '2px solid var(--scenario-b-border)',
            }}>
              <Eyebrow>Scenario B</Eyebrow>
            </div>
            <div style={{ padding: '11px 18px', textAlign: 'right' }}>
              <Eyebrow>Delta (B − A)</Eyebrow>
            </div>
            <div style={{ padding: '11px 18px', textAlign: 'right' }}>
              <Eyebrow>Delta (%)</Eyebrow>
            </div>
          </div>

          {ledgerRows.map((row, i) => (
            <LedgerRow key={i} row={row} hairlineTop={i > 0} gridTemplateColumns={LEDGER_GRID} />
          ))}

          {/* Vehicle count, cost-per-mile, and fuel-mix descriptive rows */}
          <LedgerRow
            row={{
              kind: 'count',
              label: 'Vehicle Count',
              a: resultA.fleet_totals.vehicle_count,
              b: resultB.fleet_totals.vehicle_count,
            }}
            gridTemplateColumns={LEDGER_GRID}
          />
          <LedgerRow
            row={{
              kind: 'per-mile',
              label: 'Cost / Mile',
              a: resultA.fleet_totals.fleet_cost_per_mile,
              b: resultB.fleet_totals.fleet_cost_per_mile,
            }}
            gridTemplateColumns={LEDGER_GRID}
          />
          <LedgerRow
            row={{
              kind: 'string-mix',
              label: 'Fuel Type Mix',
              a: Object.entries(resultA.fleet_metadata.fuel_type_distribution)
                .map(([k, v]) => `${k}:${v}`).join(' · ') || '—',
              b: Object.entries(resultB.fleet_metadata.fuel_type_distribution)
                .map(([k, v]) => `${k}:${v}`).join(' · ') || '—',
            }}
            gridTemplateColumns={LEDGER_GRID}
          />
        </div>
      )}

      {/* Phase 24.3.2: Year-by-Year Cost Walk — grouped bar chart on a single
          shared Y axis. Two side-by-side independent charts (the 24.3.1
          shape) made visual comparison misleading because each chart
          auto-scaled to its own scenario; bars at "the same height" could
          be very different dollar amounts. The grouped chart puts Scenario
          A and B as adjacent stacks per year, with shared segment colors and
          opacity-differentiated stacks. */}
      {resultA && resultB && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <Eyebrow>Year-by-Year Cost Walk — Scenario Comparison</Eyebrow>
            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--tco-ink-2)' }}>
              Total annual cost per scenario on a shared Y axis. Each year shows
              Scenario A on the left and Scenario B on the right. Expand the category detail
              below for the per-cost-line breakdown.
            </div>
          </div>
          <AccentCard padding="16px 18px">
            <GroupedYearByCostChart
              seriesA={{
                name: `Scenario A: ${aName}`,
                color: 'var(--scenario-a-border)',
                years: deriveYearsFromFleetResponse(resultA),
                totalFuel: (resultA.fleet_totals.total_annual_fuel_cost ?? 0) * (resultA.fleet_totals.ownership_period_years ?? 0),
                totalDepreciation: Math.max(0, (resultA.fleet_totals.total_acquisition_cost ?? 0) - (resultA.fleet_totals.total_residual_reclaimed ?? 0)),
              }}
              seriesB={{
                name: `Scenario B: ${bName}`,
                color: 'var(--scenario-b-border)',
                years: deriveYearsFromFleetResponse(resultB),
                totalFuel: (resultB.fleet_totals.total_annual_fuel_cost ?? 0) * (resultB.fleet_totals.ownership_period_years ?? 0),
                totalDepreciation: Math.max(0, (resultB.fleet_totals.total_acquisition_cost ?? 0) - (resultB.fleet_totals.total_residual_reclaimed ?? 0)),
              }}
              headerSubtitle={`A: ${resultA.fleet_totals.vehicle_count} vehicles · B: ${resultB.fleet_totals.vehicle_count} vehicles`}
            />
            <FlatCostModelNote />
          </AccentCard>
        </div>
      )}

      {/* 1.7 Break-even callout */}
      {comparison && resultA && resultB && (
        <BreakEvenSection
          info={comparison.comparison.break_even}
          aName={aName}
          bName={bName}
          resultA={resultA}
          resultB={resultB}
          ownershipYears={Math.max(ownershipPeriodA, ownershipPeriodB)}
        />
      )}

      {/* 1.8 What-If sensitivity quad */}
      {comparison && (
        <WhatIfSensitivity
          tests={comparison.comparison.sensitivity_tests}
          aName={aName}
          bName={bName}
        />
      )}

      {/* 1.9 Per-vehicle-type breakdown */}
      {resultA && resultB && (
        <PerVehicleTypeBreakdown resultA={resultA} resultB={resultB} aName={aName} bName={bName} />
      )}

      {/* 1.10 Expand rows — per-scenario line items */}
      {resultA && resultB && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <ScenarioLineItemList title={`Scenario A: ${aName}`} result={resultA} />
          <ScenarioLineItemList title={`Scenario B: ${bName}`} result={resultB} />
        </div>
      )}
    </div>
  );
}

// ─── Inline button styles (used only on this page) ──────────────────────────
const primaryBtn = {
  background: 'var(--tco-ink)',
  color: 'var(--tco-bg)',
  border: 'none',
  borderRadius: 6,
  padding: '10px 24px',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const redBtn = {
  ...primaryBtn,
  background: 'var(--tco-neg)',
  color: 'var(--tco-surface)',
};

// Phase 25.1: shared model-note rendered under year-by-year stacked bars
// so users understand the operating-cost flat-line vs depreciation curve
// asymmetry visible on the chart. Exported so the personal compare and
// fleet single-scenario views can mount the same wording.
export function FlatCostModelNote() {
  return (
    <div
      data-testid="flat-cost-model-note"
      style={{
        marginTop: 10,
        fontSize: 11,
        lineHeight: 1.45,
        color: 'var(--tco-ink-3)',
        fontStyle: 'italic',
      }}
    >
      Maintenance scales with accumulated mileage and vocation intensity,
      with warranty suppression during coverage periods. Repair costs
      reflect three warranty zones: full coverage (B2B active), partial
      coverage (powertrain only, ~35% exposed), and full exposure
      (post-warranty). Fuel includes annual inflation. Depreciation
      follows the Argonne ANL curve.
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ScenarioPicker({
  label, scenarioKey, value, onChange, scenarios,
}: {
  label: string;
  scenarioKey: 'a' | 'b';
  value: string | number | '';
  onChange: (v: string | number | '') => void;
  scenarios: ScenarioSummary[];
}) {
  const stripe = scenarioKey === 'a' ? 'ink' : 'red';
  const tintBg = scenarioKey === 'a' ? 'var(--scenario-a-tint)' : 'var(--scenario-b-tint)';
  return (
    <AccentCard stripe={stripe} padding="14px 18px" style={{ background: tintBg }}>
      <Eyebrow style={{ marginBottom: 8 }}>{label}</Eyebrow>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          fontSize: 15,
          fontWeight: 500,
          color: 'var(--tco-ink)',
          fontFamily: 'inherit',
          padding: 0,
          appearance: 'none',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        <option value="">— select scenario —</option>
        {scenarios.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
    </AccentCard>
  );
}

function OwnershipPeriodChip({
  aYears, bYears, aName, bName, info,
}: {
  aYears: number;
  bYears: number;
  aName: string;
  bName: string;
  info: ScenarioComparisonResponse['comparison']['ownership_period'];
}) {
  const periodsDiffer = !info.both_uniform_and_equal;
  return (
    <div
      style={{
        background: periodsDiffer ? 'var(--tco-neg-soft)' : 'var(--tco-surface)',
        border: `1px solid ${periodsDiffer ? 'var(--tco-neg)' : 'var(--tco-line)'}`,
        borderRadius: 'var(--tco-radius)',
        padding: '12px 18px',
        fontSize: 13,
      }}
      className="tco-num"
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'baseline' }}>
        <div>
          <Eyebrow style={{ display: 'inline', marginRight: 8 }}>Breakeven Years</Eyebrow>
          <span style={{ fontWeight: 500, color: 'var(--tco-ink)' }}>{aName}: </span>
          {aYears.toFixed(1)}yr
          {info.scenario_a_consistency === 'mixed' && info.scenario_a_range && (
            <span style={{ color: 'var(--tco-ink-3)', fontSize: 12, marginLeft: 6 }}>
              (range {info.scenario_a_range.min}-{info.scenario_a_range.max}, mixed)
            </span>
          )}
        </div>
        <div style={{ color: 'var(--tco-ink-2)' }}>
          <span style={{ fontWeight: 500, color: 'var(--tco-ink)' }}>{bName}: </span>
          {bYears.toFixed(1)}yr
          {info.scenario_b_consistency === 'mixed' && info.scenario_b_range && (
            <span style={{ color: 'var(--tco-ink-3)', fontSize: 12, marginLeft: 6 }}>
              (range {info.scenario_b_range.min}-{info.scenario_b_range.max}, mixed)
            </span>
          )}
        </div>
      </div>
      {periodsDiffer && (
        <div style={{ color: 'var(--tco-neg)', fontSize: 12, marginTop: 8 }}>
          Comparing scenarios with different ownership periods.
          Per-year and per-mile metrics enable apples-to-apples comparison.
        </div>
      )}
    </div>
  );
}

// Phase 25.1: small green "WINNER" pill rendered top-right of the lower-TCO
// scenario card in the headline. Uses --tco-accent (existing emerald) so it
// stays consistent with delta-favorable coloring elsewhere on the page.
function WinnerBadge({ testId }: { testId?: string }) {
  return (
    <span
      data-testid={testId}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        background: 'var(--tco-accent)',
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.10em',
        padding: '3px 8px',
        borderRadius: 999,
        textTransform: 'uppercase',
        lineHeight: 1,
      }}
    >
      ★ Winner
    </span>
  );
}

function HeadlineCard({
  aName, bName, aTotal, bTotal, dollarDelta, pctDelta,
  aPerVehicle, bPerVehicle, topDrivers, horizonYears,
}: {
  aName: string;
  bName: string;
  aTotal: number;
  bTotal: number;
  dollarDelta: number;
  pctDelta: number;
  aPerVehicle: number | null;
  bPerVehicle: number | null;
  topDrivers: TopDriver[];
  horizonYears: number;
}) {
  // dollar_delta sign convention from API: positive => B's TCO is higher
  // than A's (A wins). Negative => B wins. See backend
  // services/scenario_comparison.py + Phase 22.5 sign-convention tests.
  const aWins = dollarDelta > 0;
  const tied = Math.abs(dollarDelta) < 1;
  const winnerName = tied ? null : aWins ? aName : bName;
  const horizonLabel = horizonYears > 0
    ? `${Math.round(horizonYears)} year${Math.round(horizonYears) === 1 ? '' : 's'}`
    : 'the ownership period';
  const absDollar = Math.abs(Math.round(dollarDelta));
  const absPct = Math.abs(pctDelta);

  return (
    <AccentCard stripe="ink">
      <Eyebrow style={{ marginBottom: 14 }}>Headline TCO Delta</Eyebrow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: 32 }}>
        <div
          data-testid="headline-scenario-a"
          data-winner={aWins && !tied ? 'true' : 'false'}
          style={{
            position: 'relative',
            background: 'var(--scenario-a-tint)',
            borderLeft: '3px solid var(--scenario-a-border)',
            borderRadius: 'var(--tco-radius-sm)',
            padding: '12px 14px',
          }}
        >
          {aWins && !tied && <WinnerBadge testId="winner-badge-a" />}
          <Stat
            label="Scenario A"
            value={fmtMoneyPlain(aTotal)}
            sub={`${aName}${aPerVehicle != null ? ` · ${fmtMoneyPlain(aPerVehicle)}/v` : ''}`}
          />
        </div>
        <div
          data-testid="headline-scenario-b"
          data-winner={!aWins && !tied ? 'true' : 'false'}
          style={{
            position: 'relative',
            background: 'var(--scenario-b-tint)',
            borderLeft: '3px solid var(--scenario-b-border)',
            borderRadius: 'var(--tco-radius-sm)',
            padding: '12px 14px',
          }}
        >
          {!aWins && !tied && <WinnerBadge testId="winner-badge-b" />}
          <Stat
            label="Scenario B"
            value={fmtMoneyPlain(bTotal)}
            sub={`${bName}${bPerVehicle != null ? ` · ${fmtMoneyPlain(bPerVehicle)}/v` : ''}`}
          />
        </div>
        <div>
          <Eyebrow>Delta</Eyebrow>
          <div
            data-testid="headline-delta"
            className="tco-num"
            style={{
              fontSize: 36,
              fontWeight: 600,
              marginTop: 4,
              color: 'var(--tco-accent)',
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
            }}
          >
            {tied ? '$0' : '$' + absDollar.toLocaleString('en-US')}
            <span style={{ fontSize: 15, color: 'var(--tco-ink-3)', marginLeft: 8, fontWeight: 400 }}>
              ({absPct.toFixed(1)}%)
            </span>
          </div>
          <div
            data-testid="headline-delta-caption"
            style={{ fontSize: 13, color: 'var(--tco-ink-2)', marginTop: 6, lineHeight: 1.45 }}
          >
            {tied
              ? 'Scenarios are equivalent in total cost.'
              : <>
                  <strong style={{ color: 'var(--tco-ink)', fontWeight: 600 }}>{winnerName}</strong>
                  {` saves $${absDollar.toLocaleString('en-US')} (${absPct.toFixed(1)}%) over ${horizonLabel}`}
                </>}
          </div>
        </div>
      </div>

      {/* Phase 22.5 top drivers — $ + winner caption, never raw % of total. */}
      {topDrivers.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <DisclosureRow label="Why? Top drivers of the delta" labelFontSize={11}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {topDrivers.map((d) => {
                const driverWinner = d.dollar_delta > 0 ? aName : d.dollar_delta < 0 ? bName : null;
                const driverColor = d.dollar_delta > 0
                  ? 'var(--tco-accent)'
                  : d.dollar_delta < 0
                    ? 'var(--tco-neg)'
                    : 'var(--tco-ink-3)';
                return (
                  <div key={d.category} style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    paddingBottom: 6,
                    borderBottom: '1px solid var(--tco-line-soft)',
                  }}>
                    <span style={{
                      color: 'var(--tco-ink-2)',
                      textTransform: 'capitalize',
                    }}>
                      {d.category.replace(/_/g, ' ')}
                    </span>
                    <span className="tco-num">
                      <span style={{ color: driverColor, fontWeight: 600 }}>
                        ${Math.abs(Math.round(d.dollar_delta)).toLocaleString('en-US')}
                      </span>
                      <span style={{ color: 'var(--tco-ink-3)', marginLeft: 8, fontSize: 12 }}>
                        {driverWinner ? `${driverWinner} wins` : 'tie'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </DisclosureRow>
        </div>
      )}
    </AccentCard>
  );
}

function BreakEvenSection({
  info, aName, bName, resultA, resultB, ownershipYears,
}: {
  info: ScenarioComparisonResponse['comparison']['break_even'];
  aName: string;
  bName: string;
  resultA: FleetTCOResponse;
  resultB: FleetTCOResponse;
  ownershipYears: number;
}) {
  // Phase 25: break-even reframed as an explanatory callout, not a hero.
  // Hidden entirely when purchase prices are equal — no meaningful break-even
  // exists when there is no upfront premium to recover.
  const aAcq = resultA.fleet_totals.total_acquisition_cost ?? 0;
  const bAcq = resultB.fleet_totals.total_acquisition_cost ?? 0;
  const aCount = resultA.fleet_totals.vehicle_count;
  const bCount = resultB.fleet_totals.vehicle_count;
  const acqDelta = Math.abs(bAcq - aAcq);
  const higherPriceName = bAcq > aAcq ? bName : aName;
  // Phase 25.1: tint the higher-price scenario name in its identity color so
  // the user can track which scenario the break-even prose is describing.
  const higherPriceScenario: 'a' | 'b' = bAcq > aAcq ? 'b' : 'a';
  const higherPriceTintBorder =
    higherPriceScenario === 'a' ? 'var(--scenario-a-border)' : 'var(--scenario-b-border)';
  const higherPriceTintBg =
    higherPriceScenario === 'a' ? 'var(--scenario-a-tint)' : 'var(--scenario-b-tint)';

  // Fleet-aware basis: if vehicle counts differ, express the premium per
  // vehicle so the framing remains apples-to-apples.
  const countsDiffer = aCount !== bCount && aCount > 0 && bCount > 0;
  const aPerVehicleAcq = aCount > 0 ? aAcq / aCount : aAcq;
  const bPerVehicleAcq = bCount > 0 ? bAcq / bCount : bAcq;
  const perVehicleDelta = Math.abs(bPerVehicleAcq - aPerVehicleAcq);
  const deltaToShow = countsDiffer ? perVehicleDelta : acqDelta;
  const basisHint = countsDiffer ? ' per vehicle' : '';

  // Hide the callout entirely when purchase prices are equal. Use $50 as the
  // floor (rounding fuzz from per-vehicle math); below that there's no
  // meaningful premium to recover.
  if (acqDelta < 50) return null;

  if (!info.applicable) {
    return (
      <AccentCard padding="14px 18px">
        <Eyebrow style={{ marginBottom: 4 }}>Break-Even</Eyebrow>
        <div style={{ fontSize: 13, color: 'var(--tco-ink-2)' }}>{info.rationale}</div>
      </AccentCard>
    );
  }

  // Build the prose. Three cases:
  //   1) break-even within horizon: "recovers premium by Year N"
  //   2) no crossover: "does not recover premium within N-year horizon"
  const headline = 'When does the higher upfront cost pay off?';
  const horizonLabel = ownershipYears > 0 ? `${Math.round(ownershipYears)}-year` : 'ownership';
  const tintedName = (
    <span
      data-testid="break-even-scenario-name"
      data-scenario={higherPriceScenario}
      style={{
        background: higherPriceTintBg,
        borderBottom: `2px solid ${higherPriceTintBorder}`,
        padding: '0 4px',
        fontWeight: 600,
        color: 'var(--tco-ink)',
      }}
    >
      {higherPriceName}
    </span>
  );

  return (
    <AccentCard padding="14px 18px">
      <Eyebrow style={{ marginBottom: 6 }}>Break-Even</Eyebrow>
      <div
        data-testid="break-even-headline"
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--tco-ink)',
          marginBottom: 6,
          letterSpacing: '-0.005em',
        }}
      >
        {headline}
      </div>
      <div
        data-testid="break-even-body"
        style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--tco-ink-2)' }}
      >
        {info.break_even_year !== null ? (
          <>
            {tintedName} costs {fmtMoneyPlain(deltaToShow)}{basisHint} more to
            purchase but has lower operating costs. It recovers that premium
            through savings by Year {info.break_even_year} — after which it's
            the cheaper vehicle to own overall.
          </>
        ) : (
          <>
            At current costs, {tintedName} does not recover its {fmtMoneyPlain(deltaToShow)}{basisHint} price
            premium within the {horizonLabel} horizon.
          </>
        )}
      </div>
      {info.cumulative_costs_by_year.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <DisclosureRow label="Year-by-year cumulative costs">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }} className="tco-num">
                <thead>
                  <tr>
                    <th style={cellHeader}>Year</th>
                    <th style={{ ...cellHeader, textAlign: 'right' }}>{aName}</th>
                    <th style={{ ...cellHeader, textAlign: 'right' }}>{bName}</th>
                    <th style={{ ...cellHeader, textAlign: 'right' }}>Δ (B − A)</th>
                  </tr>
                </thead>
                <tbody>
                  {info.cumulative_costs_by_year.map((pt) => {
                    const d = pt.scenario_b - pt.scenario_a;
                    const dCol = Math.abs(d) < 1 ? 'var(--tco-ink-2)' : d > 0 ? 'var(--tco-neg)' : 'var(--tco-accent)';
                    return (
                      <tr key={pt.year}>
                        <td style={cellBody}>{pt.year}</td>
                        <td style={{ ...cellBody, textAlign: 'right' }}>{fmtMoney(pt.scenario_a)}</td>
                        <td style={{ ...cellBody, textAlign: 'right' }}>{fmtMoney(pt.scenario_b)}</td>
                        <td style={{ ...cellBody, textAlign: 'right', color: dCol }}>{fmtSignedDelta(d)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DisclosureRow>
        </div>
      )}
    </AccentCard>
  );
}

// Phase 25: range bars by kind. The backend always emits four paired tests
// (Fuel ±20%, Mileage ±30%); pair them by kind so each variable gets a
// horizontal bar with pessimistic/optimistic ends and a winner statement at
// each end. Compare-only: requires both scenario TCOs, which is the data
// contract this component already takes.
function WhatIfSensitivity({
  tests, aName, bName,
}: {
  tests: SensitivityTestResult[];
  aName: string;
  bName: string;
}) {
  if (!tests.length) return null;
  const groups: { kind: 'fuel' | 'mileage'; label: string; shortLabel: string; lowMultiplier: number; highMultiplier: number }[] = [
    { kind: 'fuel', label: 'Fuel price sensitivity', shortLabel: 'fuel', lowMultiplier: 0.8, highMultiplier: 1.2 },
    { kind: 'mileage', label: 'Annual mileage sensitivity', shortLabel: 'mileage', lowMultiplier: 0.7, highMultiplier: 1.3 },
  ];
  const byKindAndMult = (k: string, mult: number) =>
    tests.find((t) => t.kind === k && Math.abs(t.multiplier - mult) < 0.001);
  return (
    <AccentCard>
      <Eyebrow style={{ marginBottom: 6 }}>What If…</Eyebrow>
      <div style={{ fontSize: 13, color: 'var(--tco-ink-2)', marginBottom: 18 }}>
        How do the results change if key assumptions shift?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {groups.map((g) => {
          const low = byKindAndMult(g.kind, g.lowMultiplier);
          const high = byKindAndMult(g.kind, g.highMultiplier);
          if (!low || !high) return null;
          const lowPct = Math.round((g.lowMultiplier - 1) * 100);
          const highPct = Math.round((g.highMultiplier - 1) * 100);
          const winnerFlipped = low.winner !== high.winner;
          return (
            <SensitivityRangeCard
              key={g.kind}
              label={g.label}
              shortLabel={g.shortLabel}
              lowPct={lowPct}
              highPct={highPct}
              lowResult={low}
              highResult={high}
              aName={aName}
              bName={bName}
              winnerFlipped={winnerFlipped}
            />
          );
        })}
      </div>
    </AccentCard>
  );
}

// Phase 25.3: two-column range display per variable. The card itself is
// always on a neutral surface — no `--scenario-*-tint` background — so
// the "what changes when an assumption shifts" story reads against the
// page paper instead of inheriting one scenario's identity color (Phase
// 25.1 inadvertently bled rose onto every B-winning cell). Identity is
// carried solely by the 3px left border on each end-cell in the winner's
// scenario color (Phase 25.4: the winner name itself is neutral
// `--tco-ink` so it doesn't double-signal scenario identity).
// v1.42.5: binary stability signal. Fleet operators read amber as "bad" not
// "small savings", so the prior fragile/amber tier created confusion. Color
// now communicates only "does the same scenario still win?" — the dollar
// amount communicates magnitude. Green = same winner as baseline, red + ⚠
// = winner flips.
const STABILITY_GREEN = 'var(--tco-accent)';   // emerald — stable
const STABILITY_RED   = 'var(--tco-neg)';      // red — flipped

type Stability = 'stable' | 'flipped';

function stabilityColor(s: Stability): string {
  switch (s) {
    case 'stable':  return STABILITY_GREEN;
    case 'flipped': return STABILITY_RED;
  }
}

function SensitivityRangeCard({
  label, shortLabel, lowPct, highPct, lowResult, highResult, aName, bName, winnerFlipped,
}: {
  label: string;
  shortLabel: string;
  lowPct: number;
  highPct: number;
  lowResult: SensitivityTestResult;
  highResult: SensitivityTestResult;
  aName: string;
  bName: string;
  winnerFlipped: boolean;
}) {
  function describe(t: SensitivityTestResult): {
    winnerName: string | null;
    winnerKey: 'a' | 'b' | null;
    deltaAbs: number;
    pctAbs: number;
    isTie: boolean;
    stability: Stability;
  } {
    const deltaAbs = Math.abs(t.headline_delta);
    const isTie = deltaAbs < 500 || t.winner === 'tie';
    // Percentage relative to the loser's total (or the larger total when
    // tied) so the share is bounded and intuitive.
    const denom = Math.max(t.scenario_a_total_tco, t.scenario_b_total_tco, 1);
    const pctAbs = (deltaAbs / denom) * 100;
    // Stability vs baseline: `winner_flipped` is the per-test backend flag
    // comparing this scenario's winner to the unperturbed comparison. v1.42.5
    // collapses the prior three-tier system to binary — any unflipped result
    // is "stable" regardless of savings magnitude.
    const stability: Stability = t.winner_flipped ? 'flipped' : 'stable';
    if (isTie) return { winnerName: null, winnerKey: null, deltaAbs, pctAbs, isTie, stability };
    const winnerKey = t.winner === 'a' ? 'a' : t.winner === 'b' ? 'b' : null;
    const winnerName = winnerKey === 'a' ? aName : winnerKey === 'b' ? bName : null;
    return { winnerName, winnerKey, deltaAbs, pctAbs, isTie, stability };
  }
  const lo = describe(lowResult);
  const hi = describe(highResult);

  const lowPctLabel = lowPct > 0 ? `+${lowPct}%` : `${lowPct}%`;
  const highPctLabel = highPct > 0 ? `+${highPct}%` : `${highPct}%`;

  return (
    <div
      data-testid={`sensitivity-range-${lowResult.kind}`}
      style={{
        border: '1px solid var(--tco-line)',
        background: 'var(--tco-surface)',
        borderRadius: 'var(--tco-radius-sm)',
        padding: '14px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tco-ink)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </div>
        {winnerFlipped && (
          <div data-testid={`sensitivity-flip-${lowResult.kind}`} style={{ fontSize: 12, color: 'var(--tco-neg)', fontWeight: 500 }}>
            ⚠ Winner changes across this range
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <SensitivityEndCell
          headLabel={`At ${lowPctLabel} ${shortLabel}:`}
          result={lo}
        />
        <SensitivityEndCell
          headLabel={`At ${highPctLabel} ${shortLabel}:`}
          result={hi}
        />
      </div>
    </div>
  );
}

function SensitivityEndCell({
  headLabel, result,
}: {
  headLabel: string;
  result: {
    winnerName: string | null;
    winnerKey: 'a' | 'b' | null;
    deltaAbs: number;
    pctAbs: number;
    isTie: boolean;
    stability: Stability;
  };
}) {
  // Phase 25.3/25.4: neutral background on every cell. Scenario identity
  // is carried only by the 3px left border color, keyed to
  // `--scenario-*-border`. The winner name itself renders in neutral
  // `--tco-ink` (Phase 25.4) so it doesn't compete with the savings
  // amount's stability color.
  const identityColor = result.winnerKey === 'a'
    ? 'var(--scenario-a-border)'
    : result.winnerKey === 'b'
      ? 'var(--scenario-b-border)'
      : 'var(--tco-line)';
  const amountColor = result.isTie ? 'var(--tco-ink-2)' : stabilityColor(result.stability);
  const showFlipIcon = !result.isTie && result.stability === 'flipped';
  return (
    <div
      style={{
        background: 'var(--tco-surface)',
        borderLeft: `3px solid ${identityColor}`,
        borderRadius: 'var(--tco-radius-sm)',
        padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--tco-ink-3)', letterSpacing: '0.05em', marginBottom: 6, textTransform: 'uppercase' }}>
        {headLabel}
      </div>
      {result.isTie ? (
        <div style={{ fontSize: 13, color: 'var(--tco-ink-2)' }}>Scenarios equivalent at this level</div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--tco-ink)', fontWeight: 600, marginBottom: 4 }}>
            {result.winnerName}
          </div>
          <div className="tco-num" style={{ fontSize: 20, fontWeight: 600, color: amountColor, lineHeight: 1.1 }}>
            {showFlipIcon && <span style={{ marginRight: 4 }}>⚠</span>}
            saves {fmtMoney(result.deltaAbs)}
          </div>
          <div className="tco-num" style={{ fontSize: 11, color: 'var(--tco-ink-3)', marginTop: 2 }}>
            ({result.pctAbs.toFixed(1)}%)
          </div>
        </>
      )}
    </div>
  );
}

function PerVehicleTypeBreakdown({
  resultA, resultB, aName, bName,
}: {
  resultA: FleetTCOResponse;
  resultB: FleetTCOResponse;
  aName: string;
  bName: string;
}) {
  const groupByLabel = (lines: FleetLineOutput[]) => {
    const out: Record<string, { qty: number; perVehicleTco: number }> = {};
    for (const li of lines) {
      const key = li.vehicle_label;
      const perVehicle = li.quantity > 0 ? li.subtotal_tco.tco_over_period / li.quantity : 0;
      const existing = out[key];
      if (existing) {
        existing.qty += li.quantity;
      } else {
        out[key] = { qty: li.quantity, perVehicleTco: perVehicle };
      }
    }
    return out;
  };
  const aGrouped = groupByLabel(resultA.line_items);
  const bGrouped = groupByLabel(resultB.line_items);
  const allLabels = Array.from(new Set([...Object.keys(aGrouped), ...Object.keys(bGrouped)]));
  if (allLabels.length === 0) return null;
  return (
    <div
      style={{
        background: 'var(--tco-surface)',
        border: '1px solid var(--tco-line)',
        borderRadius: 'var(--tco-radius)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 20px 8px' }}>
        <Eyebrow>Per-Vehicle-Type Breakdown</Eyebrow>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 0.7fr 1.2fr 0.7fr 1.2fr 1fr',
          background: 'var(--tco-line-soft)',
          borderTop: '1px solid var(--tco-line)',
          borderBottom: '1px solid var(--tco-line)',
        }}
      >
        <div style={{ padding: '10px 16px' }}><Eyebrow style={{ fontSize: 10.5 }}>Vehicle</Eyebrow></div>
        <div style={{
          padding: '10px 16px',
          textAlign: 'right',
          background: 'var(--scenario-a-tint)',
          borderBottom: '2px solid var(--scenario-a-border)',
        }}>
          <Eyebrow style={{ fontSize: 10.5 }}>{aName} qty</Eyebrow>
        </div>
        <div style={{
          padding: '10px 16px',
          textAlign: 'right',
          background: 'var(--scenario-a-tint)',
          borderBottom: '2px solid var(--scenario-a-border)',
        }}>
          <Eyebrow style={{ fontSize: 10.5 }}>{aName} per-vehicle TCO</Eyebrow>
        </div>
        <div style={{
          padding: '10px 16px',
          textAlign: 'right',
          background: 'var(--scenario-b-tint)',
          borderBottom: '2px solid var(--scenario-b-border)',
        }}>
          <Eyebrow style={{ fontSize: 10.5 }}>{bName} qty</Eyebrow>
        </div>
        <div style={{
          padding: '10px 16px',
          textAlign: 'right',
          background: 'var(--scenario-b-tint)',
          borderBottom: '2px solid var(--scenario-b-border)',
        }}>
          <Eyebrow style={{ fontSize: 10.5 }}>{bName} per-vehicle TCO</Eyebrow>
        </div>
        <div style={{ padding: '10px 16px', textAlign: 'right' }}><Eyebrow style={{ fontSize: 10.5 }}>Δ per-vehicle</Eyebrow></div>
      </div>
      {allLabels.map((label, i) => {
        const a = aGrouped[label];
        const b = bGrouped[label];
        const perDelta = (b?.perVehicleTco ?? 0) - (a?.perVehicleTco ?? 0);
        const dCol = !a || !b
          ? 'var(--tco-ink-3)'
          : Math.abs(perDelta) < 1 ? 'var(--tco-ink-2)'
            : perDelta > 0 ? 'var(--tco-neg)' : 'var(--tco-accent)';
        return (
          <div
            key={label}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 0.7fr 1.2fr 0.7fr 1.2fr 1fr',
              borderTop: i === 0 ? 'none' : '1px solid var(--tco-line-soft)',
            }}
          >
            <div style={breakdownCell}>{label}</div>
            <div style={{ ...breakdownCell, textAlign: 'right', color: a ? 'var(--tco-ink)' : 'var(--tco-ink-3)' }}>
              {a?.qty ?? '—'}
            </div>
            <div style={{ ...breakdownCell, textAlign: 'right', color: a ? 'var(--tco-ink)' : 'var(--tco-ink-3)' }}>
              {a ? fmtMoney(a.perVehicleTco) : '—'}
            </div>
            <div style={{ ...breakdownCell, textAlign: 'right', color: b ? 'var(--tco-ink)' : 'var(--tco-ink-3)' }}>
              {b?.qty ?? '—'}
            </div>
            <div style={{ ...breakdownCell, textAlign: 'right', color: b ? 'var(--tco-ink)' : 'var(--tco-ink-3)' }}>
              {b ? fmtMoney(b.perVehicleTco) : '—'}
            </div>
            <div style={{ ...breakdownCell, textAlign: 'right', color: dCol }}>
              {a && b ? fmtSignedDelta(perDelta) : '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScenarioLineItemList({ title, result }: { title: string; result: FleetTCOResponse }) {
  return (
    <AccentCard padding="14px 18px">
      <DisclosureRow label={`${title} — ${result.line_items.length} line item${result.line_items.length === 1 ? '' : 's'}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {result.line_items.map((li, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'var(--tco-bg)',
                border: '1px solid var(--tco-line-soft)',
                padding: '10px 12px',
                borderRadius: 'var(--tco-radius-sm)',
                fontSize: 13,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--tco-ink)', fontWeight: 500 }}>{li.line_item_name}</div>
                <div style={{ color: 'var(--tco-ink-3)', fontSize: 11 }}>{li.vehicle_label}</div>
              </div>
              <div className="tco-num" style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                <div style={{ color: 'var(--tco-ink)' }}>
                  {li.quantity} × {fmtMoney(li.subtotal_tco.acquisition_cost / Math.max(1, li.quantity))}
                </div>
                <div style={{ color: 'var(--tco-ink-3)', fontSize: 11 }}>
                  subtotal {fmtMoney(li.subtotal_tco.tco_over_period)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </DisclosureRow>
    </AccentCard>
  );
}

function SkeletonHeadline() {
  return (
    <AccentCard stripe="ink">
      <div style={{ background: 'var(--tco-line-soft)', height: 14, width: 180, borderRadius: 4, marginBottom: 14 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: 32 }}>
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <div style={{ background: 'var(--tco-line-soft)', height: 11, width: 80, borderRadius: 4, marginBottom: 8 }} />
            <div style={{ background: 'var(--tco-line-soft)', height: 28, width: 140, borderRadius: 4 }} />
          </div>
        ))}
      </div>
    </AccentCard>
  );
}

function SkeletonLedger() {
  return (
    <div
      style={{
        background: 'var(--tco-surface)',
        border: '1px solid var(--tco-line)',
        borderRadius: 'var(--tco-radius)',
        padding: 24,
      }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: 16,
            paddingBlock: 12,
            borderBottom: '1px solid var(--tco-line-soft)',
          }}
        >
          {[0, 1, 2, 3].map((j) => (
            <div key={j} style={{ background: 'var(--tco-line-soft)', height: 14, borderRadius: 4 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

const cellHeader: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.10em',
  textTransform: 'uppercase',
  color: 'var(--tco-ink-3)',
  textAlign: 'left',
  padding: '8px 10px',
};
const cellBody: React.CSSProperties = {
  borderTop: '1px solid var(--tco-line-soft)',
  padding: '6px 10px',
  color: 'var(--tco-ink-2)',
};
const breakdownCell: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: "'tnum'",
  color: 'var(--tco-ink)',
};
